/**
 * Contenedor de pantalla del kiosco (design-system §8.2 y research R-3): ocupa exactamente la
 * pantalla (`h-dvh`, sin scroll ni rebote), fondo Crema, safe areas (`fm-safe`) y tres slots:
 *
 * - `header`: franja superior (marca pequeña, indicador de estado…).
 * - contenido: `title` (Nunito 800 28 px), `lead` (secundario) y `children`, centrados en ambos ejes
 *   por defecto (la pantalla se lee a distancia); `align="start"` los alinea arriba.
 * - `footer`: franja inferior (reloj pequeño, punto de conexión en la esquina inferior derecha).
 *
 * El gutter lateral, el ritmo vertical y el padding de las franjas salen de las variables de
 * `styles/index.css` (`--fm-screen-*`), que cambian solas por clase de pantalla: teléfono en
 * vertical (gutter 16 px), pantalla baja / teléfono en horizontal (ritmo comprimido) y
 * tablet/escritorio (los valores del design system). Ninguna pantalla hace scroll nunca.
 *
 * `data-state` viaja al nodo raíz para que los E2E y el CSS distingan el `MarkerState` en curso.
 * `onPointerDown` permite al kiosco recuperar la pantalla completa con un toque (`kiosk/fullscreen.ts`).
 */
import { cn } from './cn';

import type { PointerEventHandler, ReactNode } from 'react';

export interface ScreenProps {
  title?: string;
  lead?: string;
  header?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  /** `center` (por defecto) centra el contenido; `start` lo apila desde arriba. */
  align?: 'start' | 'center';
  className?: string;
  contentClassName?: string;
  onPointerDown?: PointerEventHandler<HTMLDivElement>;
  'data-testid'?: string;
  'data-state'?: string;
}

export function Screen({
  title,
  lead,
  header,
  footer,
  children,
  align = 'center',
  className,
  contentClassName,
  onPointerDown,
  'data-testid': testId,
  'data-state': state,
}: ScreenProps) {
  return (
    <div
      data-testid={testId}
      data-state={state}
      onPointerDown={onPointerDown}
      className={cn(
        'fm-safe flex h-dvh w-full flex-col overflow-hidden bg-fm-bg text-fm-text',
        className,
      )}
    >
      {header ? (
        <header className="fm-screen-header flex shrink-0 items-center justify-between gap-fm-4">
          {header}
        </header>
      ) : null}

      <main
        className={cn(
          'fm-screen-main flex min-h-0 flex-1 flex-col items-center text-center',
          align === 'center' ? 'justify-center' : 'justify-start',
          contentClassName,
        )}
      >
        {title ? <ScreenTitle>{title}</ScreenTitle> : null}
        {lead ? <ScreenLead>{lead}</ScreenLead> : null}
        {children}
      </main>

      {footer ? (
        <footer className="fm-screen-footer flex shrink-0 items-end justify-between gap-fm-4">
          {footer}
        </footer>
      ) : null}
    </div>
  );
}

export function ScreenTitle({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <h1 className={cn('font-display text-fm-2xl font-extrabold text-fm-text', className)}>
      {children}
    </h1>
  );
}

export function ScreenLead({ className, children }: { className?: string; children: ReactNode }) {
  return <p className={cn('text-fm-lg text-fm-text-secondary', className)}>{children}</p>;
}
